import { useEffect, useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import { useSignedLandingAssets } from '../../hooks/useSignedLandingAssets'
import { useIsMobileViewport } from '../../hooks/useMediaQuery'
import { APP_NAME } from '../../constants/brand'
import { createNadDebug } from '../../utils/debugLog'
import {
  getVideoMutedPreference,
  setVideoMutedPreference,
} from '../../utils/videoMutePreference'
import type { LandingStoryOpenTrigger } from '../../analytics/generated/analyticsEvents'
import { trackLandingStoryOpened, trackLandingStoryPreviewStarted } from './landingAnalytics'

const mobileLoadingLog = createNadDebug('dw:mobile-ui-loading')
const mutePreferenceLog = createNadDebug('dw:hover-video-audio:preference')

interface GalleryItem {
  slug: string
  genre: string
  title: string
  description: string
  /** true = video (hover-to-play), false = static image */
  isVideo: boolean
}

/**
 * Poster shown before the hover-to-play video loads.
 *
 * Derived 1:1 from the item `slug` so a card's still frame always matches the
 * video it covers. The posters (`public/landing-v2/gallery/<slug>.jpg`) are
 * frames extracted from each corresponding gallery video. The previous
 * `mosaic-*.jpg` set was a hand-maintained, differently-named list left over
 * from the partner-IP gallery (RFC-0832) — after the gallery was swapped to
 * original stories the images no longer matched their videos (e.g. a caped
 * "Krypto" superdog still frame sat on the `corgi-garden` card). Keying the
 * poster off the slug removes that drift class entirely: add a gallery item and
 * drop a `<slug>.jpg` next to it.
 */
function posterUrl(slug: string): string {
  return `/landing-v2/gallery/${slug}.jpg`
}

/** What opened the expanded player, captured at the click for `landing_story_opened`. */
interface StoryOpenRequest {
  slug: string
  position: number
  trigger: LandingStoryOpenTrigger
  previewWasActive: boolean
}

const GALLERY_ITEMS: GalleryItem[] = [
  {
    slug: 'letters-from-home',
    genre: 'Drama',
    title: 'Letters From Home',
    description: 'Words that bridge worlds apart',
    isVideo: true,
  },
  {
    slug: 'eternal-night-ascends',
    genre: 'Fantasy',
    title: 'Eternal Night Ascends',
    description: 'Darkness rises over a forgotten realm',
    isVideo: true,
  },
  {
    slug: 'corgi-garden',
    genre: 'Fantasy',
    title: 'Corgi Garden',
    description: 'Here we meet again',
    isVideo: true,
  },
  {
    slug: 'where-the-crows-gather',
    genre: 'Action',
    title: 'Where The Crows Gather',
    description: 'Mystery lurks in the shadows',
    isVideo: true,
  },
  {
    slug: 'bolt-and-spark',
    genre: 'Animated series',
    title: 'Bolt & Spark',
    description: 'A duo that lights up the sky',
    isVideo: true,
  },
]


function ExpandIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
    </svg>
  )
}

function VolumeOffIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
    </svg>
  )
}

function VolumeOnIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072M17.95 6.05a8 8 0 010 11.9" />
    </svg>
  )
}

/** Render one hover-play gallery card with a browser-local audio preference. */
function VideoCard({
  item,
  position,
  videoUrl,
  shouldLoadVideo,
  onArm,
  onExpand,
}: {
  item: GalleryItem
  /** 1-based position in the gallery (analytics only). */
  position: number
  /** Signed GCS URL for this gallery item's video. Empty string while loading. */
  videoUrl: string
  shouldLoadVideo: boolean
  onArm: (slug: string) => void
  onExpand: (request: StoryOpenRequest) => void
}) {
  const { t } = useTranslation('landing')
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isHovering, setIsHovering] = useState(false)
  // RFC-1577 preview analytics: the pointer is over the card, this hover has
  // already reported its preview, and the preview is currently playing.
  const isHoveringRef = useRef(false)
  const previewReportedRef = useRef(false)
  const previewActiveRef = useRef(false)
  const mutePreferenceId = `landing-gallery:${item.slug}`
  const [isMuted, setIsMuted] = useState(() =>
    getVideoMutedPreference(mutePreferenceId),
  )

  const handleMouseEnter = useCallback(() => {
    isHoveringRef.current = true
    previewReportedRef.current = false
    setIsHovering(true)
    onArm(item.slug)
  }, [item.slug, onArm])

  const handleMouseLeave = useCallback(() => {
    isHoveringRef.current = false
    previewActiveRef.current = false
    setIsHovering(false)
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
    }
  }, [])

  useEffect(() => {
    if (!isHovering || !videoUrl) return
    const video = videoRef.current
    if (!video) return
    const preferredMuted = getVideoMutedPreference(mutePreferenceId)
    video.muted = preferredMuted
    setIsMuted(preferredMuted)
    mutePreferenceLog({
      event: 'hover-playback-mute-resolved',
      surface: 'landing-gallery',
      videoId: item.slug,
      muted: preferredMuted,
    })
    video.play().catch((error) => {
      mutePreferenceLog({
        event: 'hover-playback-rejected',
        surface: 'landing-gallery',
        videoId: item.slug,
        reason: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
      })
    })
  }, [isHovering, item.slug, mutePreferenceId, videoUrl])

  const handleToggleMute = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    const video = videoRef.current
    const next = video ? !video.muted : !isMuted
    if (video) {
      video.muted = next
      if (!next) video.volume = 1
    }
    setIsMuted(next)
    setVideoMutedPreference(mutePreferenceId, next)
    mutePreferenceLog({
      event: 'mute-preference-updated',
      surface: 'landing-gallery',
      videoId: item.slug,
      muted: next,
      volume: video?.volume ?? null,
    })

    // If audible hover autoplay was blocked, this click supplies the browser
    // activation needed to retry with the newly selected audio state.
    video?.play().then(
      () => {
        mutePreferenceLog({
          event: 'audio-toggle-playback-resolved',
          surface: 'landing-gallery',
          videoId: item.slug,
          muted: video.muted,
          volume: video.volume,
        })
      },
      (error) => {
        mutePreferenceLog({
          event: 'audio-toggle-playback-rejected',
          surface: 'landing-gallery',
          videoId: item.slug,
          muted: video.muted,
          volume: video.volume,
          reason: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
        })
      },
    )
  }, [isMuted, item.slug, mutePreferenceId])

  // RFC-1577 `landing_story_preview_started`: only on the media's real
  // `playing` event during a hover (not on the play() call), once per hover —
  // a loop or a mute-toggle replay inside the same hover does not re-report.
  const handlePlaying = useCallback((e: React.SyntheticEvent<HTMLVideoElement>) => {
    if (!isHoveringRef.current) return
    previewActiveRef.current = true
    if (previewReportedRef.current) return
    previewReportedRef.current = true
    trackLandingStoryPreviewStarted({
      story_id: item.slug,
      card_position: position,
      trigger: 'hover',
      muted: e.currentTarget.muted,
    })
  }, [item.slug, position])

  const handlePause = useCallback(() => {
    previewActiveRef.current = false
  }, [])

  const requestOpen = useCallback(
    (trigger: LandingStoryOpenTrigger) => {
      onArm(item.slug)
      onExpand({
        slug: item.slug,
        position,
        trigger,
        previewWasActive: previewActiveRef.current,
      })
    },
    [item.slug, onArm, onExpand, position],
  )

  const handleExpand = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      requestOpen('fullscreen_button')
    },
    [requestOpen]
  )

  return (
    <div
      data-testid={`landing-gallery-card-${item.slug}`}
      className="group w-[220px] flex-shrink-0 sm:w-[240px]"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={() => onArm(item.slug)}
    >
      <div
        className="relative h-[390px] w-full cursor-pointer overflow-hidden rounded-2xl border border-white/10 sm:h-[426px]"
        onClick={() => requestOpen('card_click')}
      >
        <img
          src={posterUrl(item.slug)}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        {/* Omit <video> until the signed URL is ready — an empty src
            re-requests the current page in some browsers. */}
        {shouldLoadVideo && videoUrl && (
          <video
            ref={videoRef}
            src={videoUrl}
            muted={isMuted}
            loop
            playsInline
            preload="none"
            onPlaying={handlePlaying}
            onPause={handlePause}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent from-[50%] to-black/80 pointer-events-none" />

        {/* Top-right action buttons — visible on hover */}
        <div
          className={`
            absolute top-3 right-3 flex flex-col gap-2 transition-opacity duration-300
            ${isHovering ? 'opacity-100' : 'opacity-0'}
          `}
        >
          <button
            onClick={handleExpand}
            className="w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm border border-white/20
                       flex items-center justify-center text-white/80 hover:text-white hover:bg-black/70 transition-all"
            title={t('inspirationV2.watchFullscreen')}
          >
            <ExpandIcon className="w-4 h-4" />
          </button>
          <button
            onClick={handleToggleMute}
            className="w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm border border-white/20
                       flex items-center justify-center text-white/80 hover:text-white hover:bg-black/70 transition-all"
            title={isMuted ? t('inspirationV2.unmute') : t('inspirationV2.mute')}
          >
            {isMuted ? (
              <VolumeOffIcon className="w-4 h-4" />
            ) : (
              <VolumeOnIcon className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Genre pill */}
        <div className="absolute bottom-16 left-4">
          <div className="backdrop-blur-[6px] bg-white/[0.18] border border-white/[0.35] rounded-full px-3 py-1">
            <span className="font-[Fustat] font-medium text-xs text-white leading-[1.2]">
              {t(`inspirationV2.gallery.${item.slug}.genre`)}
            </span>
          </div>
        </div>

        {/* Title + description */}
        <div className="absolute bottom-0 left-0 right-0 p-4 flex flex-col gap-1">
          <h3 className="font-[Fustat] font-semibold text-sm text-white leading-[1.2]">
            {t(`inspirationV2.gallery.${item.slug}.title`)}
          </h3>
          <p className="font-[Fustat] text-sm text-[#a1a1aa] leading-[1.3]">
            {t(`inspirationV2.gallery.${item.slug}.description`)}
          </p>
        </div>
      </div>
    </div>
  )
}

export default function InspirationSectionV2() {
  const { t } = useTranslation('landing')
  const [activeVideoSlug, setActiveVideoSlug] = useState<string | null>(null)
  const [armedSlugs, setArmedSlugs] = useState<Set<string>>(() => new Set())
  const isMobileViewport = useIsMobileViewport()
  const shouldSignAssets = armedSlugs.size > 0 || activeVideoSlug !== null
  const { urls: signedAssets, optimizedUrls } = useSignedLandingAssets({ enabled: shouldSignAssets })
  const signedVideoUrl = useCallback(
    (slug: string) => (isMobileViewport && optimizedUrls[slug]) || signedAssets[slug] || '',
    [isMobileViewport, optimizedUrls, signedAssets],
  )
  const activeVideoUrl = activeVideoSlug ? signedVideoUrl(activeVideoSlug) : undefined
  // The click that opened the expanded player, until its open is reported.
  const pendingOpenRef = useRef<StoryOpenRequest | null>(null)

  const openStory = useCallback((request: StoryOpenRequest) => {
    pendingOpenRef.current = request
    setActiveVideoSlug(request.slug)
  }, [])

  const closeStory = useCallback(() => {
    pendingOpenRef.current = null
    setActiveVideoSlug(null)
  }, [])

  // RFC-1577 `landing_story_opened`: when the expanded player itself is
  // on screen (its signed URL resolved), once per open.
  useEffect(() => {
    const request = pendingOpenRef.current
    if (!request || !activeVideoUrl || request.slug !== activeVideoSlug) return
    pendingOpenRef.current = null
    trackLandingStoryOpened({
      story_id: request.slug,
      card_position: request.position,
      trigger: request.trigger,
      preview_was_active: request.previewWasActive,
    })
  }, [activeVideoSlug, activeVideoUrl])

  const armVideo = useCallback((slug: string) => {
    setArmedSlugs((prev) => {
      if (prev.has(slug)) return prev
      const next = new Set(prev)
      next.add(slug)
      mobileLoadingLog({
        event: 'gallery-video-armed',
        feature: 'mobile-ui-loading',
        slug,
        armed_count: next.size,
      })
      return next
    })
  }, [])

  return (
    <section className="relative bg-black px-4 py-[60px] sm:px-6 md:px-20">
      <div className="mx-auto flex max-w-[1280px] flex-col items-center gap-12 md:gap-20">
        {/* Header */}
        <div className="flex max-w-[880px] flex-col items-center gap-6">
          <h2 className="font-[Fustat] text-hero font-semibold leading-[1.08] text-center text-white md:text-display-md md:leading-[1.05]">
            {t('inspirationV2.heading', { appName: APP_NAME })}
          </h2>
        </div>

        {/* Gallery — horizontal scroll reel */}
        <div
          className="flex gap-6 overflow-x-auto w-full scrollbar-hide pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {GALLERY_ITEMS.map((item, index) => (
            <VideoCard
              key={item.slug}
              item={item}
              position={index + 1}
              videoUrl={signedVideoUrl(item.slug)}
              shouldLoadVideo={armedSlugs.has(item.slug) || activeVideoSlug === item.slug}
              onArm={armVideo}
              onExpand={openStory}
            />
          ))}
        </div>
      </div>

      {/* Bottom divider */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-white/10" />

      {/* Fullscreen Video Modal */}
      {activeVideoSlug &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm md:p-8"
            onClick={closeStory}
          >
            <div
              className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeStory}
                className="absolute -top-12 right-0 text-white/60 hover:text-white transition-colors"
              >
                <svg
                  className="w-8 h-8"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              {activeVideoUrl ? (
                <video
                  src={activeVideoUrl}
                  controls
                  autoPlay
                  className="max-h-[85vh] max-w-full rounded-lg bg-black shadow-2xl"
                />
              ) : (
                <div className="flex min-h-[280px] w-full max-w-xl items-center justify-center rounded-lg border border-white/10 bg-black text-sm text-white/60">
                  {t('inspirationV2.loadingPreview')}
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </section>
  )
}
